"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import YouTubeEmbed from "@/components/youtube-embed";
import { useJourneyData } from "@/hooks/use-journey-data";
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

const SETUP_STEPS = [
  {
    title: "Create a Cal.com Account",
    description:
      "Cal.com helps you schedule viewings professionally with listing agents - they'll get instant confirmations and calendar invites.",
    image: "/images/cal-setup/signup.png",
    link: "https://cal.com",
    type: "image",
  },
  {
    title: "Set Your Availability",
    description:
      "Configure your weekly schedule and when you're available for property viewings.",
    videoId: "UsICIcyGIBA",
    link: "https://app.cal.com/availability",
    type: "video",
  },
  {
    title: "Create House Showing Event Type",
    description: "Set up a dedicated event type for property viewings.",
    image: "/images/cal-setup/create-event-type.png",
    link: "https://app.cal.com/event-types",
    type: "image",
  },
  {
    title: "Add Your Calendar Link",
    description: "Paste your Cal.com link to enable scheduling.",
    image: "/images/cal-setup/copy-link.png",
    type: "input",
  },
] as const;

export function CalSetupModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const { journeyData, updateJourney } = useJourneyData();
  const [calLink, setCalLink] = useState(journeyData?.calLink || "");

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    // Remove cal.com domain if present
    const cleanedLink = pastedText.replace(
      /^(?:https?:\/\/)?(?:www\.)?cal\.com\//,
      "",
    );
    setCalLink(cleanedLink);
  };

  const handleSaveLink = async () => {
    await updateJourney.mutateAsync({ calLink });
    onOpenChange(false);
  };

  const isLastStep = currentStep === SETUP_STEPS.length - 1;
  const currentStepData = SETUP_STEPS[currentStep];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Up Your Calendar</DialogTitle>
          <DialogDescription>
            Follow these steps to connect your calendar for property viewings
          </DialogDescription>
        </DialogHeader>

        <div className="relative mt-4">
          {currentStepData.type === "input" ? (
            <div className="space-y-4">
              <div className="aspect-video rounded-lg border bg-muted overflow-hidden">
                <img
                  src={currentStepData.image}
                  alt={currentStepData.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <Input
                placeholder="your-name/house-showing"
                value={calLink}
                onChange={(e) => setCalLink(e.target.value)}
                onPaste={handlePaste}
                className="w-full"
              />
              <p className="text-sm text-muted-foreground">
                Enter your Cal.com link (e.g., "john-doe/house-showing")
              </p>
            </div>
          ) : (
            <>
              <div className="aspect-video rounded-lg border bg-muted overflow-hidden">
                {currentStepData.type === "video" ? (
                  <YouTubeEmbed
                    videoId={currentStepData.videoId}
                    title={currentStepData.title}
                  />
                ) : (
                  <img
                    src={currentStepData.image}
                    alt={currentStepData.title}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>

              <div className="mt-4 text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <h3 className="font-semibold">{currentStepData.title}</h3>
                  {currentStepData.link && (
                    <a
                      href={currentStepData.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {currentStepData.description}
                </p>
              </div>
            </>
          )}

          <div className="mt-6 flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((prev) => prev - 1)}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            {isLastStep ? (
              <Button
                onClick={handleSaveLink}
                disabled={!calLink || updateJourney.isPending}
                className="bg-black hover:bg-black/90 text-white"
              >
                {updateJourney.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Calendar Link"
                )}
              </Button>
            ) : currentStep === SETUP_STEPS.length - 2 ? (
              <Button onClick={() => setCurrentStep((prev) => prev + 1)}>
                Add Your Link
              </Button>
            ) : (
              <Button onClick={() => setCurrentStep((prev) => prev + 1)}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
